import React from "react";
import { BellRing, ChevronRight, ConciergeBell, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MobileScreenSettingsWithRelations } from "@/types/mobile-settings";

interface ChatDisplayPreviewProps {
  settings: MobileScreenSettingsWithRelations;
}

export const ChatDisplayPreview: React.FC<ChatDisplayPreviewProps> = ({
  settings,
}) => {
  const { t } = useTranslation();
  const previewRef = React.useRef<HTMLDivElement>(null);
  const appTextColor = settings.appTextColor || "#111827";
  const appBgColor = settings.appBgColor || "#FFFFFF";
  const services = settings.mobileScreenSettingsChatServices ?? [];
  const showServices = settings.isChatDisplayEnabled && services.length > 0;

  React.useEffect(() => {
    const preview = previewRef.current;

    if (!preview) return;

    preview.style.setProperty("--guest-app-text", appTextColor);
    preview.style.setProperty("--guest-app-bg", appBgColor);
  }, [appBgColor, appTextColor]);

  return (
    <div
      ref={previewRef}
      className="flex h-[650px] flex-col bg-[var(--guest-app-bg)] text-[var(--guest-app-text)]"
    >
      <div className="flex items-center justify-center border-b border-current/10 px-4 py-3">
        <h3 className="text-sm font-bold">
          {t("mobileSettings.customization.chatDisplay.previewTitle")}
        </h3>
      </div>

      <div className="flex flex-1 flex-col gap-5 overflow-hidden px-4 py-5">
        <div className="bg-muted text-muted-foreground self-center rounded-full px-3 py-1 text-[10px]">
          {t("mobileSettings.customization.chatDisplay.today")}
        </div>

        <div className="flex items-end gap-2">
          <div className="bg-primary text-primary-foreground flex size-9 shrink-0 items-center justify-center rounded-full">
            <BellRing className="size-4" />
          </div>
          <div className="bg-card flex flex-col gap-2 rounded-2xl rounded-bl-sm border p-3 text-xs shadow-sm">
            <span className="text-muted-foreground text-[10px]">
              {t("mobileSettings.customization.chatDisplay.concierge")}
            </span>
            <p>
              {t("mobileSettings.customization.chatDisplay.welcomeMessage")}
            </p>
          </div>
        </div>

        {showServices ? (
          <div className="bg-card mt-auto flex flex-col rounded-2xl border shadow-lg">
            <div className="flex items-center gap-2 border-b border-current/10 px-4 py-3">
              <ConciergeBell className="size-4" />
              <p className="text-xs font-bold">
                {t("mobileSettings.customization.chatDisplay.services")}
              </p>
            </div>
            {services.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 border-b border-current/10 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">
                    {item.service.name}
                  </p>
                  <p className="text-muted-foreground truncate text-[10px]">
                    {item.service.category.name}
                  </p>
                </div>
                <ChevronRight className="text-muted-foreground size-4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground mt-auto rounded-xl border border-dashed p-4 text-center text-[10px]">
            {t("mobileSettings.customization.chatDisplay.previewEmpty")}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-current/10 px-3 py-3">
        <button
          type="button"
          className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-full"
          aria-label={t("mobileSettings.customization.chatDisplay.services")}
        >
          <ConciergeBell className="size-4" />
        </button>
        <div className="text-muted-foreground flex h-9 flex-1 items-center justify-between rounded-full border border-current/30 px-3 text-[10px]">
          {t("mobileSettings.customization.chatDisplay.messagePlaceholder")}
          <Paperclip className="size-3.5" />
        </div>
      </div>
    </div>
  );
};
